import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, KeyRound, Mail, Check } from "lucide-react";
import { useAuth, LOGO_URL } from "../context/AuthContext";
import { authApi } from "../lib/api";
import { toast } from "sonner";

export default function Login() {
    const { user, config, loginWithPassword, loading } = useAuth();
    const navigate = useNavigate();
    const [mode, setMode] = useState("password"); // "password" | "magic"
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [magicSent, setMagicSent] = useState(false);

    useEffect(() => {
        if (!loading && user) navigate("/dashboard", { replace: true });
    }, [user, loading, navigate]);

    const handlePassword = async (e) => {
        e.preventDefault();
        if (!email || !password) return;
        setBusy(true);
        try {
            await loginWithPassword(email.trim().toLowerCase(), password);
            navigate("/dashboard");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Invalid email or password",
            );
        } finally {
            setBusy(false);
        }
    };

    const handleMagic = async (e) => {
        e.preventDefault();
        if (!email) return;
        setBusy(true);
        try {
            await authApi.requestMagicLink(email.trim().toLowerCase());
            setMagicSent(true);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Could not send magic link",
            );
        } finally {
            setBusy(false);
        }
    };

    const domainsLabel =
        (config.allowed_domains || []).map((d) => "@" + d).join("  |  ") ||
        "@911cellphonebank.org";

    const tabClass = (active) =>
        [
            "flex-1 font-display text-sm px-4 py-3 brut-border inline-flex items-center justify-center gap-2",
            active
                ? "bg-[#E8241A] text-white"
                : "bg-white text-black hover:bg-black hover:text-white",
        ].join(" ");

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-8">
            <div className="w-full max-w-md">
                {/* Logo hero — red with black offset shadow */}
                <div
                    className="bg-[#E8241A] brut-border brut-shadow p-6 sm:p-8 mb-[-20px] relative z-10"
                    data-testid="login-logo-card"
                >
                    <img
                        src={LOGO_URL}
                        alt="911 Cellphone Bank"
                        className="w-full h-auto"
                    />
                </div>

                {/* Login card */}
                <div
                    className="bg-white brut-border brut-shadow relative"
                    data-testid="login-card"
                >
                    <span className="brut-tape">Employees Only</span>

                    <div className="p-6 sm:p-8 pt-10">
                        <h1 className="font-display text-3xl sm:text-4xl leading-none">
                            Sign in.
                        </h1>
                        <p className="mt-3 text-sm">
                            Access your work sites from one place.
                        </p>

                        <div className="mt-5 flex items-start gap-2 text-xs bg-[#FAFAF7] brut-border p-3">
                            <ShieldCheck
                                size={16}
                                strokeWidth={3}
                                className="mt-0.5 shrink-0"
                            />
                            <span>
                                Only emails from{" "}
                                <strong>{domainsLabel}</strong> may sign in.
                            </span>
                        </div>

                        {/* Mode tabs */}
                        <div className="mt-6 flex gap-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setMode("password");
                                    setMagicSent(false);
                                }}
                                className={tabClass(mode === "password")}
                                data-testid="tab-password"
                            >
                                <KeyRound size={16} strokeWidth={3} />
                                Password
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setMode("magic");
                                    setMagicSent(false);
                                }}
                                className={tabClass(mode === "magic")}
                                data-testid="tab-magic"
                                disabled={!config.magic_link_enabled}
                                title={
                                    config.magic_link_enabled
                                        ? ""
                                        : "Magic link email is not configured"
                                }
                            >
                                <Mail size={16} strokeWidth={3} />
                                Magic Link
                            </button>
                        </div>

                        {mode === "password" ? (
                            <form
                                onSubmit={handlePassword}
                                className="mt-5 space-y-3"
                            >
                                <Field label="Work email">
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                        required
                                        placeholder="you@911cellphonebank.org"
                                        className="w-full px-4 py-3 brut-border bg-white text-black placeholder:text-black/40 focus:bg-[#FAFAF7] outline-none"
                                        data-testid="login-email-input"
                                    />
                                </Field>
                                <Field label="Password">
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) =>
                                            setPassword(e.target.value)
                                        }
                                        required
                                        className="w-full px-4 py-3 brut-border bg-white text-black placeholder:text-black/40 focus:bg-[#FAFAF7] outline-none"
                                        data-testid="login-password-input"
                                    />
                                </Field>
                                <button
                                    type="submit"
                                    disabled={busy}
                                    className="w-full bg-[#E8241A] text-white font-display text-lg sm:text-xl px-6 py-4 brut-border brut-shadow brut-press inline-flex items-center justify-center gap-2 disabled:opacity-60"
                                    data-testid="login-submit-btn"
                                >
                                    {busy ? "Signing in…" : "Enter Dashboard"}{" "}
                                    <ArrowRight size={20} strokeWidth={3} />
                                </button>
                                <div className="pt-1 text-center">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate("/auth/forgot")
                                        }
                                        className="font-display text-[11px] tracking-widest underline"
                                        data-testid="forgot-password-link"
                                    >
                                        Forgot password?
                                    </button>
                                </div>
                            </form>
                        ) : magicSent ? (
                            <div
                                className="mt-5 bg-[#FAFAF7] brut-border p-4 space-y-2"
                                data-testid="magic-sent-card"
                            >
                                <div className="inline-flex items-center gap-2 font-display">
                                    <Check size={18} strokeWidth={3} />
                                    Check your inbox
                                </div>
                                <p className="text-xs">
                                    If <strong>{email}</strong> is an employee,
                                    we sent a one-time sign-in link. It expires
                                    in 15 minutes.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setMagicSent(false)}
                                    className="font-display text-xs underline"
                                    data-testid="magic-sent-reset"
                                >
                                    Use a different email
                                </button>
                            </div>
                        ) : (
                            <form
                                onSubmit={handleMagic}
                                className="mt-5 space-y-3"
                            >
                                <Field label="Work email">
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                        required
                                        placeholder="you@911cellphonebank.org"
                                        className="w-full px-4 py-3 brut-border bg-white text-black placeholder:text-black/40 focus:bg-[#FAFAF7] outline-none"
                                        data-testid="magic-email-input"
                                    />
                                </Field>
                                <button
                                    type="submit"
                                    disabled={busy}
                                    className="w-full bg-[#E8241A] text-white font-display text-lg sm:text-xl px-6 py-4 brut-border brut-shadow brut-press inline-flex items-center justify-center gap-2 disabled:opacity-60"
                                    data-testid="magic-submit-btn"
                                >
                                    {busy ? "Sending…" : "Email Me A Link"}{" "}
                                    <Mail size={20} strokeWidth={3} />
                                </button>
                            </form>
                        )}
                    </div>
                </div>

                <p className="mt-6 text-center text-[11px] uppercase tracking-widest opacity-70">
                    © 911 Cellphone Bank — Internal Tools
                </p>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block font-display text-[11px] tracking-widest mb-1">
                {label}
            </span>
            {children}
        </label>
    );
}
